import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type FocusEventHandler,
  type KeyboardEventHandler,
  type ReactNode,
  type Ref,
} from 'react';
import {
  AccountBalanceWalletOutlined,
  AccountTreeOutlined,
  AddOutlined,
  ArrowBackOutlined,
  ArrowForwardOutlined,
  AttachFileOutlined,
  CalendarMonthOutlined,
  CallMergeOutlined,
  CheckCircleOutlined,
  ChecklistOutlined,
  EventOutlined,
  EditOutlined,
  MoreHorizOutlined,
  ExpandMoreOutlined,
  InfoOutlined,
  LockOutlined,
  MoreVertOutlined,
  NotesOutlined,
  NumbersOutlined,
  PersonOutlined,
  LinkOutlined,
  ShortTextOutlined,
} from '@mui/icons-material';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  InputBase,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
  type SelectChangeEvent,
  type SxProps,
  type Theme,
} from '@mui/material';
import type {
  ApprovalLabelSummary,
  WorkflowFieldDefinition,
  WorkflowFieldType,
  WorkflowRequestDraftApprovalStep,
} from './model';
import { AnchoredMenu, ChoiceControl, createInputSurfaceStyles, designTokens, DialogSurface, Field, HelpPopover, PageHeaderTitle, ValueBadge, type FieldControlState } from '../../design-system';
import { workflowApprovalLabelBadgeTone } from './workflowApprovalLabelPresentation';
import { WorkflowApprovalBranchRail, WorkflowApprovalConnector } from './WorkflowApprovalFlowParts';
import {
  groupWorkflowApprovalSteps,
  insertWorkflowApprovalStepInStage,
  mergeWorkflowApprovalStageWithPrevious,
  moveWorkflowApprovalStage,
  normalizeWorkflowApprovalSteps,
  splitWorkflowApprovalStageAt,
} from './workflowApprovalStages';

export const WORKFLOW_REQUEST_TITLE_MAX_LENGTH = 200;

const inheritedPageTitleTypography = {
  fontFamily: 'inherit',
  fontSize: 'inherit',
  lineHeight: 'inherit',
  fontWeight: 'inherit',
  letterSpacing: 'inherit',
} as const;

const visuallyHiddenSx = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  p: 0,
  m: '-1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
} as const;

const quietControlSx = {
  width: '100%',
  minWidth: 0,
  minHeight: designTokens.layout.controlMinHeight,
  p: `${designTokens.space.xs}px`,
  ...designTokens.typography.ui,
  [designTokens.media.touchTarget]: { minHeight: designTokens.layout.touchTargetMinSize },
  color: designTokens.color.primaryText,
  '&.Mui-disabled': { opacity: designTokens.button.disabledOpacity },
  '& .MuiInputBase-input.Mui-disabled, & .MuiSelect-select.Mui-disabled': {
    WebkitTextFillColor: 'inherit',
  },
  '& .MuiInputBase-input': {
    px: 0,
    py: 0,
    ...designTokens.typography.ui,
    [designTokens.media.touchTarget]: { fontSize: designTokens.typography.body.fontSize },
  },
  '& .MuiSelect-select': {
    px: 0,
    py: 0,
    minHeight: 'auto',
    ...designTokens.typography.ui,
    [designTokens.media.touchTarget]: { fontSize: designTokens.typography.body.fontSize },
  },
} satisfies SxProps<Theme>;

function sxOverrides(sx?: SxProps<Theme>) {
  if (!sx) return [];
  return Array.isArray(sx) ? sx : [sx];
}

function normalizeDOMID(value: string) {
  return value.replace(/[^a-zA-Z0-9_-]/g, '');
}

export type WorkflowRequestTitleInputProps = {
  value: string;
  onChange: (value: string) => void;
  leading?: ReactNode;
  supporting?: ReactNode;
  supportingPlacement?: 'below' | 'inline';
  label?: string;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  readOnly?: boolean;
  autoFocus?: boolean;
  name?: string;
  inputRef?: Ref<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  sx?: SxProps<Theme>;
};

/** PageHeader의 title slot에 넣는 문서형 제목 입력입니다. */
export function WorkflowRequestTitleInput({
  value,
  onChange,
  leading,
  supporting,
  supportingPlacement = 'below',
  label = '요청 제목',
  placeholder = '제목을 입력하세요',
  error,
  disabled = false,
  readOnly = false,
  autoFocus = false,
  name,
  inputRef,
  onBlur,
  onKeyDown,
  sx,
}: WorkflowRequestTitleInputProps) {
  const generatedID = useId();
  const inputID = `workflow-request-title-${normalizeDOMID(generatedID)}`;
  const errorID = `${inputID}-error`;
  const localInputRef = useRef<HTMLInputElement | null>(null);
  const editBaselineRef = useRef(value);
  const [editing, setEditing] = useState(autoFocus);
  const titleTextRef = useRef<HTMLSpanElement>(null);
  const [titleClipped, setTitleClipped] = useState(false);
  const [fullTitleOpen, setFullTitleOpen] = useState(false);

  useEffect(() => {
    const element = titleTextRef.current;
    if (!readOnly || !element) { setTitleClipped(false); return; }
    const measure = () => setTitleClipped(element.scrollHeight > element.clientHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [readOnly, editing, value]);

  useEffect(() => {
    if (!editing) return;
    localInputRef.current?.focus();
    localInputRef.current?.select();
  }, [editing]);

  const assignInputRef = (element: HTMLInputElement | null) => {
    localInputRef.current = element;
    if (typeof inputRef === 'function') inputRef(element);
    else if (inputRef) (inputRef as { current: HTMLInputElement | null }).current = element;
  };
  const beginEditing = () => {
    if (disabled || readOnly) return;
    editBaselineRef.current = value;
    setEditing(true);
  };
  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (event) => {
    onKeyDown?.(event);
    if (event.defaultPrevented) return;
    if (event.key === 'Enter') {
      event.preventDefault();
      event.currentTarget.blur();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      // 제목 편집 취소가 바깥 작성 dialog의 닫기로 이어지지 않게 합니다.
      event.stopPropagation();
      onChange(editBaselineRef.current);
      setEditing(false);
    }
  };

  const titleContent = <Box
    component="span"
    sx={[
      {
        ...(editing ? createInputSurfaceStyles({ variant: 'inset', disabled, invalid: Boolean(error) }) : {}),
        position: 'relative',
        display: 'inline-flex',
        width: editing ? 'clamp(14rem, 68vw, 42rem)' : 'auto',
        maxWidth: '100%',
        minWidth: 0,
        minHeight: {
          xs: `${designTokens.layout.pageHeaderFirstLineHeightCompact}px`,
          sm: `${designTokens.layout.pageHeaderFirstLineHeight}px`,
        },
        alignItems: 'center',
        color: editing ? (error ? designTokens.color.danger : designTokens.color.primaryText) : designTokens.color.pageHeading,
        bgcolor: editing ? disabled ? designTokens.color.overviewSurface : designTokens.color.canvas : undefined,
        px: editing ? 1 : 0,
        '& input': {
          p: 0,
          color: 'inherit',
          ...inheritedPageTitleTypography,
          textOverflow: 'ellipsis',
        },
        '& input::placeholder': {
          color: designTokens.color.mutedText,
          opacity: 1,
        },
      },
      ...sxOverrides(sx),
    ]}
  >
    <Box component="label" htmlFor={inputID} sx={visuallyHiddenSx}>{label}</Box>
    {editing ? (
      <InputBase
        slots={{ root: 'span' }}
        id={inputID}
        name={name}
        value={value}
        inputRef={assignInputRef}
        disabled={disabled}
        readOnly={readOnly}
        error={Boolean(error)}
        placeholder={placeholder}
        onBlur={(event) => {
          setEditing(false);
          onBlur?.(event);
        }}
        onKeyDown={handleKeyDown}
        onChange={(event) => onChange(event.target.value)}
        inputProps={{
          maxLength: WORKFLOW_REQUEST_TITLE_MAX_LENGTH,
          'aria-label': label,
          'aria-invalid': Boolean(error),
          'aria-describedby': error ? errorID : undefined,
        }}
        sx={{ width: '100%', bgcolor: 'transparent', ...inheritedPageTitleTypography }}
      />
    ) : (
      <Typography
        component="span"
        ref={titleTextRef}
        sx={{
          minWidth: 0,
          overflowWrap: 'anywhere',
          wordBreak: 'keep-all',
          whiteSpace: 'normal',
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: 2,
          overflow: 'hidden',
          color: value.trim() ? 'inherit' : designTokens.color.pageSupportingText,
          ...inheritedPageTitleTypography,
        }}
      >{value.trim() || placeholder}</Typography>
    )}
  </Box>;

  return <><PageHeaderTitle
    leading={leading}
    titleOverflow="wrap"
    supportingPlacement={error ? 'below' : supportingPlacement}
    title={titleContent}
    trailingAction={readOnly && titleClipped ? {
      label: `${label} 전체 보기`,
      icon: <MoreHorizOutlined />,
      onSelect: () => setFullTitleOpen(true),
    } : !editing && !disabled && !readOnly ? {
      label: `${label} 수정`,
      icon: <EditOutlined />,
      onSelect: beginEditing,
    } : undefined}
    supporting={error || supporting ? <Stack component="span" spacing={0.25}>
      {supporting}
      {error ? <Typography
        component="span"
        id={errorID}
        sx={{ color: designTokens.color.pageErrorText, ...designTokens.typography.supporting, fontWeight: 500 }}
      >{error}</Typography> : null}
    </Stack> : undefined}
  /><DialogSurface open={fullTitleOpen} onClose={() => setFullTitleOpen(false)} title={`${label} 전체 보기`} closeLabel="전체 제목 닫기" size="compact" mobilePresentation="dialog">
    <Typography sx={{ overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>{value.trim() || placeholder}</Typography>
  </DialogSurface></>;
}

export type WorkflowLongTextRendererProps = {
  field: WorkflowFieldDefinition;
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
  controlID: string;
  labelID: string;
  messageID: string;
  describedBy?: string;
  error?: string;
  maxLength?: number;
  placeholder: string;
};

export type WorkflowRequestFieldsProps = {
  controlIdPrefix?: string;
  fields: readonly WorkflowFieldDefinition[];
  values: Readonly<Record<string, unknown>>;
  onChange: (fieldKey: string, value: unknown) => void;
  errors?: Readonly<Record<string, string | undefined>>;
  disabled?: boolean;
  ariaLabel?: string;
  renderLongText?: (props: WorkflowLongTextRendererProps) => ReactNode;
  renderControl?: (props: WorkflowCustomFieldRendererProps) => ReactNode | undefined;
  sx?: SxProps<Theme>;
};

export type WorkflowCustomFieldRendererProps = {
  field: WorkflowFieldDefinition;
  value: unknown;
  onChange: (value: unknown) => void;
  disabled: boolean;
  error?: string;
  state: FieldControlState;
};

type FieldOption = { value: string; label?: string };

function fieldOptions(field: WorkflowFieldDefinition): FieldOption[] {
  if (!Array.isArray(field.config?.options)) return [];
  return field.config.options.filter((candidate): candidate is FieldOption => (
    Boolean(candidate)
    && typeof candidate === 'object'
    && typeof (candidate as FieldOption).value === 'string'
  ));
}

function fieldMaximumLength(field: WorkflowFieldDefinition) {
  const value = field.validation?.maxLength;
  return typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : undefined;
}

function fieldValidationNumber(field: WorkflowFieldDefinition, key: 'min' | 'max') {
  const value = field.validation?.[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function fieldTypeIcon(type: WorkflowFieldType) {
  const iconSx = { fontSize: 19 };
  switch (type) {
    case 'SHORT_TEXT': return <ShortTextOutlined sx={iconSx} />;
    case 'LONG_TEXT': return <NotesOutlined sx={iconSx} />;
    case 'NUMBER': return <NumbersOutlined sx={iconSx} />;
    case 'CURRENCY': return <AccountBalanceWalletOutlined sx={iconSx} />;
    case 'DATE': return <CalendarMonthOutlined sx={iconSx} />;
    case 'DATE_RANGE': return <EventOutlined sx={iconSx} />;
    case 'SINGLE_SELECT': return <CheckCircleOutlined sx={iconSx} />;
    case 'MULTI_SELECT': return <ChecklistOutlined sx={iconSx} />;
    case 'PERSON': return <PersonOutlined sx={iconSx} />;
    case 'ORGANIZATION': return <AccountTreeOutlined sx={iconSx} />;
    case 'FILE': return <AttachFileOutlined sx={iconSx} />;
    case 'WORKFLOW_REFERENCE': return <LinkOutlined sx={iconSx} />;
    case 'DISPLAY_TEXT': return <InfoOutlined sx={iconSx} />;
  }
}

function fieldPlaceholder(field: WorkflowFieldDefinition) {
  if (field.type === 'PERSON') return '구성원을 선택하세요';
  if (field.type === 'ORGANIZATION') return '조직을 선택하세요';
  if (field.type === 'SINGLE_SELECT' || field.type === 'MULTI_SELECT') return '선택하세요';
  if (field.type === 'LONG_TEXT') return '내용을 작성하세요';
  if (field.type === 'FILE') return '파일을 끌어다 놓거나 선택하세요';
  if (field.type === 'WORKFLOW_REFERENCE') return '다른 워크플로우 문서를 검색하세요';
  return `${field.label} 입력`;
}

type WorkflowFieldControlProps = {
  field: WorkflowFieldDefinition;
  value: unknown;
  onChange: (value: unknown) => void;
  disabled: boolean;
  error?: string;
  state: FieldControlState;
  renderLongText?: WorkflowRequestFieldsProps['renderLongText'];
};

function WorkflowFieldControl({ field, value, onChange, disabled, error, state, renderLongText }: WorkflowFieldControlProps) {
  const fieldControlSx = {
    ...createInputSurfaceStyles({ variant: 'inset', disabled, invalid: Boolean(error) }),
    ...quietControlSx,
    bgcolor: designTokens.color.canvas,
  } satisfies SxProps<Theme>;
  const describedBy = error || field.helpText ? state.messageId : undefined;
  const commonInputProps = {
    'aria-labelledby': state.labelId,
    'aria-describedby': describedBy,
    'aria-invalid': Boolean(error),
  } as const;
  const placeholder = fieldPlaceholder(field);
  const maximumLength = fieldMaximumLength(field);

  if (field.type === 'PERSON' || field.type === 'ORGANIZATION') {
    return <Typography id={state.controlId} color="text.secondary" variant="body2">선택 목록을 연결할 수 없습니다.</Typography>;
  }
  if (field.type === 'LONG_TEXT') {
    if (renderLongText) {
      return renderLongText({
        field,
        value: typeof value === 'string' ? value : '',
        onChange: (next) => onChange(next),
        disabled,
        controlID: state.controlId,
        labelID: state.labelId,
        messageID: state.messageId,
        describedBy,
        error,
        maxLength: maximumLength,
        placeholder,
      });
    }
    return (
      <InputBase
        id={state.controlId}
        fullWidth
        multiline
        minRows={5}
        value={typeof value === 'string' ? value : ''}
        disabled={disabled}
        error={Boolean(error)}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        inputProps={{ ...commonInputProps, maxLength: maximumLength }}
        sx={[fieldControlSx, { alignItems: 'flex-start' }]}
      />
    );
  }

  if (field.type === 'DATE_RANGE') {
    const range = value && typeof value === 'object' && !Array.isArray(value)
      ? value as { from?: unknown; to?: unknown }
      : {};
    const from = typeof range.from === 'string' ? range.from : '';
    const to = typeof range.to === 'string' ? range.to : '';
    return (
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { sm: 'center' } }}>
        <InputBase
          id={state.controlId}
          type="date"
          value={from}
          disabled={disabled}
          error={Boolean(error)}
          onChange={(event) => onChange({ ...range, from: event.target.value })}
          inputProps={{ ...commonInputProps, 'aria-label': `${field.label} 시작` }}
          sx={fieldControlSx}
        />
        <Typography aria-hidden variant="body2" color="text.secondary">—</Typography>
        <InputBase
          type="date"
          value={to}
          disabled={disabled}
          error={Boolean(error)}
          onChange={(event) => onChange({ ...range, to: event.target.value })}
          inputProps={{ ...commonInputProps, 'aria-label': `${field.label} 종료` }}
          sx={fieldControlSx}
        />
      </Stack>
    );
  }

  if (field.type === 'SINGLE_SELECT') {
    const options = fieldOptions(field);
    return (
      <Select<string>
        id={state.controlId}
        labelId={state.labelId}
        variant="standard"
        disableUnderline
        fullWidth
        displayEmpty
        value={typeof value === 'string' ? value : ''}
        disabled={disabled}
        error={Boolean(error)}
        inputProps={{ 'aria-describedby': describedBy, 'aria-invalid': Boolean(error) }}
        onChange={(event) => onChange(event.target.value)}
        renderValue={(selected) => selected
          ? <ValueBadge label={options.find((option) => option.value === selected)?.label ?? selected} />
          : <Typography component="span" color="text.secondary">{placeholder}</Typography>}
        sx={fieldControlSx}
      >
        {!field.required ? <MenuItem value="">선택 안 함</MenuItem> : null}
        {options.map((option) => <MenuItem key={option.value} value={option.value}><ValueBadge label={option.label ?? option.value} /></MenuItem>)}
      </Select>
    );
  }

  if (field.type === 'MULTI_SELECT') {
    const options = fieldOptions(field);
    const selected = Array.isArray(value) ? value.map(String) : [];
    return (
      <Select<string[]>
        id={state.controlId}
        labelId={state.labelId}
        variant="standard"
        disableUnderline
        multiple
        fullWidth
        displayEmpty
        value={selected}
        disabled={disabled}
        error={Boolean(error)}
        inputProps={{ 'aria-describedby': describedBy, 'aria-invalid': Boolean(error) }}
        onChange={(event: SelectChangeEvent<string[]>) => {
          const next = event.target.value;
          onChange(typeof next === 'string' ? next.split(',').filter(Boolean) : next);
        }}
        renderValue={(items) => items.length
          ? <Stack component="span" direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>{items.map((item) => <ValueBadge key={item} label={options.find((option) => option.value === item)?.label ?? item} />)}</Stack>
          : <Typography component="span" color="text.secondary">{placeholder}</Typography>}
        sx={fieldControlSx}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            <ChoiceControl kind="checkbox" size="small" checked={selected.includes(option.value)} decorative />
            <ValueBadge label={option.label ?? option.value} />
          </MenuItem>
        ))}
      </Select>
    );
  }

  const numeric = field.type === 'NUMBER' || field.type === 'CURRENCY';
  const inputValue = numeric
    ? typeof value === 'number' || typeof value === 'string' ? value : ''
    : typeof value === 'string' ? value : '';
  return (
    <InputBase
      id={state.controlId}
      fullWidth
      type={field.type === 'DATE' ? 'date' : numeric ? 'number' : 'text'}
      value={inputValue}
      disabled={disabled}
      error={Boolean(error)}
      placeholder={placeholder}
      onChange={(event) => onChange(numeric && event.target.value !== '' ? Number(event.target.value) : event.target.value)}
      inputProps={{
        ...commonInputProps,
        maxLength: maximumLength,
        min: numeric ? fieldValidationNumber(field, 'min') : undefined,
        max: numeric ? fieldValidationNumber(field, 'max') : undefined,
        step: numeric ? 'any' : undefined,
      }}
      sx={fieldControlSx}
    />
  );
}

/** 양식 정의를 유지하면서 라벨 다음에 넓은 입력 영역이 이어지는 요청 필드 묶음입니다. */
export function WorkflowRequestFields({
  fields,
  values,
  onChange,
  errors = {},
  disabled = false,
  ariaLabel = '요청 내용',
  controlIdPrefix,
  renderLongText,
  renderControl,
  sx,
}: WorkflowRequestFieldsProps) {
  const generatedID = useId();
  const groupID = normalizeDOMID(controlIdPrefix ?? generatedID);

  return (
    <Stack
      component="section"
      aria-label={ariaLabel}
      spacing={{ xs: 2.5, sm: 3 }}
      sx={[
        {
          minWidth: 0,
        },
        ...sxOverrides(sx),
      ]}
    >
      {fields.map((field) => {
        const error = errors[field.key];
        const controlID = `workflow-field-${groupID}-${normalizeDOMID(field.key)}`;
        if (field.type === 'DISPLAY_TEXT') {
          return (
            <Stack
              key={field.id ?? field.key}
              data-testid="workflow-request-field-row"
              role="note"
              sx={{
                minWidth: 0,
                display: 'grid',
                gridTemplateColumns: '32px minmax(0, 1fr)',
                columnGap: 1.25,
                px: { xs: 1.5, sm: 1.75 },
                py: 1.5,
                borderRadius: designTokens.radius.surface,
                bgcolor: designTokens.color.actionSoft,
                alignItems: 'flex-start',
              }}
            >
              <Box sx={{ width: 32, height: 32, flex: '0 0 auto', display: 'grid', placeItems: 'center', borderRadius: designTokens.radius.surface, bgcolor: designTokens.color.actionSoft, color: designTokens.color.action }}>
                {fieldTypeIcon(field.type)}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{field.label}</Typography>
                {field.helpText ? <Typography variant="caption" color="text.secondary">{field.helpText}</Typography> : null}
              </Box>
            </Stack>
          );
        }

        return (
          <Field.Root
            key={field.id ?? field.key}
            data-testid="workflow-request-field-row"
            controlId={controlID}
            required={field.required}
            disabled={disabled}
            invalid={Boolean(error)}
            spacing={0}
            sx={{
              minWidth: 0,
              px: 0,
              py: 0,
            }}
          >
            <Field.Header sx={{
              mb: 0.5,
              justifyContent: 'flex-start',
            }}>
              <Field.Label>{field.label}</Field.Label>
            </Field.Header>
            <Box sx={{
              minWidth: 0,
            }}>
              <Field.Control>
                {(state) => (
                  renderControl?.({
                    field,
                    value: values[field.key],
                    onChange: (value) => onChange(field.key, value),
                    disabled,
                    error,
                    state,
                  }) ?? <WorkflowFieldControl
                      field={field}
                      value={values[field.key]}
                      onChange={(value) => onChange(field.key, value)}
                      disabled={disabled}
                      error={error}
                      state={state}
                      renderLongText={renderLongText}
                    />
                )}
              </Field.Control>
              {error || field.helpText || fieldMaximumLength(field) ? (
                <Field.Footer sx={{ mt: 0.5 }}>
                  {error || field.helpText ? <Field.Message>{error ?? field.helpText}</Field.Message> : <Box sx={{ flex: 1 }} />}
                  {fieldMaximumLength(field) ? (
                    <Field.Count>
                      {typeof values[field.key] === 'string' ? Array.from(values[field.key] as string).length : 0}/{fieldMaximumLength(field)}
                    </Field.Count>
                  ) : null}
                </Field.Footer>
              ) : null}
            </Box>
          </Field.Root>
        );
      })}
    </Stack>
  );
}

export type WorkflowApprovalLinePreview = {
  stepID?: string;
  sourceTemplateStepID?: string;
  order?: number;
  personID?: string;
  name?: string;
  jobTitle?: string;
  profilePhotoURL?: string;
  fallbackLabel?: string;
  note?: string;
  loading?: boolean;
};

export type WorkflowFixedPersonPickerRendererProps = {
  step: WorkflowRequestDraftApprovalStep;
  stepIndex: number;
  personID: string;
  preview?: WorkflowApprovalLinePreview;
  disabled: boolean;
  onChange: (personID: string) => void;
};

export type WorkflowFixedRolePickerRendererProps = {
  step: WorkflowRequestDraftApprovalStep;
  stepIndex: number;
  roleCode: string;
  preview?: WorkflowApprovalLinePreview;
  disabled: boolean;
  onChange: (roleCode: string) => void;
};

export type WorkflowApprovalLineEditorProps = {
  steps: readonly WorkflowRequestDraftApprovalStep[];
  onChange: (steps: WorkflowRequestDraftApprovalStep[]) => void;
  fields?: readonly WorkflowFieldDefinition[];
  previews?: readonly WorkflowApprovalLinePreview[];
  approvalLabels?: readonly ApprovalLabelSummary[];
  disabled?: boolean;
  error?: string;
  getStepError?: (step: WorkflowRequestDraftApprovalStep, index: number) => string | undefined;
  minimumSteps?: number;
  showAddStep?: boolean;
  showStepActions?: boolean;
  onAddStep?: () => void;
  renderFixedPersonPicker?: (props: WorkflowFixedPersonPickerRendererProps) => ReactNode;
  renderFixedRolePicker?: (props: WorkflowFixedRolePickerRendererProps) => ReactNode;
  action?: ReactNode;
  description?: ReactNode;
  title?: string;
  /** 시각적 제목은 숨기되 section의 접근성 이름은 title로 유지합니다. */
  hideTitle?: boolean;
  /** 데스크톱 결재선 surface의 최소 높이입니다. 읽기용 카드는 콘텐츠 높이를 유지합니다. */
  flowMinHeight?: number;
  sx?: SxProps<Theme>;
};

type ResolverType = WorkflowRequestDraftApprovalStep['resolverType'];

const resolverOptions: Array<{ value: ResolverType; label: string }> = [
  { value: 'REQUESTER_ORG_LEADER', label: '요청자의 조직장' },
  { value: 'ROOT_ORG_LEADER', label: '공식 최상위 조직장' },
  { value: 'FIXED_PERSON', label: '특정 구성원' },
  { value: 'FIXED_ROLE', label: '특정 역할' },
  { value: 'FORM_PERSON_FIELD', label: '양식의 구성원 필드' },
  { value: 'SUBJECT_ORG_LEADER', label: '대상 구성원의 조직장' },
];

function approvalStepKey(step: WorkflowRequestDraftApprovalStep, index: number) {
  return step.id ?? step.sourceTemplateStepId ?? `request-approval-step-${index}`;
}

const interactiveDragOriginSelector = [
  'a[href]',
  'button',
  'input',
  'select',
  'textarea',
  '[contenteditable="true"]',
  '[role="button"]',
  '[role="combobox"]',
].join(',');

function isInteractiveDragOrigin(target: EventTarget | null) {
  return target instanceof Element && target.closest(interactiveDragOriginSelector) !== null;
}

function previewForStep(
  previews: readonly WorkflowApprovalLinePreview[],
  step: WorkflowRequestDraftApprovalStep,
  index: number,
) {
  return previews.find((preview) => (
    Boolean(preview.stepID && step.id && preview.stepID === step.id)
    || Boolean(preview.sourceTemplateStepID && step.sourceTemplateStepId && preview.sourceTemplateStepID === step.sourceTemplateStepId)
    || preview.order === step.order
    || preview.order === index + 1
  ));
}

function resolverDescription(step: WorkflowRequestDraftApprovalStep) {
  switch (step.resolverType) {
    case 'REQUESTER_ORG_LEADER': return `요청자의 ${Number(step.resolverConfig?.level ?? 1)}차 조직장`;
    case 'SUBJECT_ORG_LEADER': return `선택한 구성원의 ${Number(step.resolverConfig?.level ?? 1)}차 조직장`;
    case 'ROOT_ORG_LEADER': return '공식 최상위 조직장';
    case 'FIXED_PERSON': return '지정한 구성원';
    case 'FIXED_ROLE': return '지정한 역할의 승인자';
    case 'FORM_PERSON_FIELD': return '양식에서 선택한 구성원';
  }
}

function defaultResolverConfig(type: ResolverType) {
  if (type === 'REQUESTER_ORG_LEADER' || type === 'SUBJECT_ORG_LEADER') return { level: 1 };
  return {};
}

function newApprovalStep(order: number): WorkflowRequestDraftApprovalStep {
  return {
    order,
    stageOrder: order,
    label: `${order}차 승인`,
    resolverType: 'REQUESTER_ORG_LEADER',
    resolverConfig: { level: 1 },
    missingPolicy: 'FAIL',
    selfPolicy: 'SKIP',
    duplicatePolicy: 'SKIP',
    requesterEditable: true,
  };
}

type CompactSelectOption = { value: string; label: string };

function CompactSelect({
  label,
  value,
  options,
  disabled,
  onChange,
}: {
  label: string;
  value: string;
  options: readonly CompactSelectOption[];
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <Select<string>
      aria-label={label}
      variant="standard"
      disableUnderline
      fullWidth
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
      sx={{
        ...createInputSurfaceStyles({ variant: 'inset', disabled }),
        ...quietControlSx,
        bgcolor: designTokens.color.canvas,
      }}
    >
      {options.map((option) => <MenuItem key={option.value} value={option.value}><ValueBadge label={option.label} /></MenuItem>)}
    </Select>
  );
}

function CompactInput({
  label,
  value,
  type = 'text',
  disabled,
  minimum,
  onChange,
}: {
  label: string;
  value: string | number;
  type?: 'text' | 'number';
  disabled: boolean;
  minimum?: number;
  onChange: (value: string) => void;
}) {
  return (
    <InputBase
      fullWidth
      aria-label={label}
      type={type}
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
      inputProps={{ min: minimum }}
      sx={{
        ...createInputSurfaceStyles({ variant: 'inset', disabled }),
        ...quietControlSx,
        bgcolor: designTokens.color.canvas,
      }}
    />
  );
}

function ApprovalSetting({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Stack spacing={0.6} sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 650 }}>{label}</Typography>
      {children}
    </Stack>
  );
}

function ApprovalStepDetails({
  step,
  index,
  fields,
  preview,
  approvalLabels,
  disabled,
  onUpdate,
  renderFixedPersonPicker,
  renderFixedRolePicker,
}: {
  step: WorkflowRequestDraftApprovalStep;
  index: number;
  fields: readonly WorkflowFieldDefinition[];
  preview?: WorkflowApprovalLinePreview;
  approvalLabels: readonly ApprovalLabelSummary[];
  disabled: boolean;
  onUpdate: (patch: Partial<WorkflowRequestDraftApprovalStep>) => void;
  renderFixedPersonPicker?: WorkflowApprovalLineEditorProps['renderFixedPersonPicker'];
  renderFixedRolePicker?: WorkflowApprovalLineEditorProps['renderFixedRolePicker'];
}) {
  const config = step.resolverConfig ?? {};
  const personFields = fields.filter((field) => field.type === 'PERSON');
  const updateConfig = (patch: Record<string, unknown>) => onUpdate({ resolverConfig: { ...config, ...patch } });
  const fieldKey = typeof config.fieldKey === 'string' ? config.fieldKey : '';
  const personID = typeof config.personId === 'string' ? config.personId : '';
  const roleCode = typeof config.roleCode === 'string' ? config.roleCode : '';

  return (
    <Box
      sx={{
        mt: 1.25,
        p: { xs: 1.25, sm: 1.5 },
        borderRadius: designTokens.radius.surface,
        bgcolor: designTokens.color.sidebarSurface,
        display: 'grid',
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
        gap: 1.25,
      }}
    >
      <ApprovalSetting label="단계 이름">
        <CompactInput
          label={`${index + 1}단계 이름`}
          value={step.label}
          disabled={disabled}
          onChange={(value) => onUpdate({ label: value })}
        />
      </ApprovalSetting>

      <ApprovalSetting label="승인 라벨">
        <CompactSelect
          label={`${step.label} 승인 라벨`}
          value={step.approvalLabelId ?? step.approvalLabel?.id ?? ''}
          options={approvalLabels.map((label) => ({ value: label.id ?? '', label: label.name }))}
          disabled={disabled}
          onChange={(value) => {
            const approvalLabel = approvalLabels.find((label) => (label.id ?? '') === value)
              ?? { name: '승인', tone: 'GREEN' as const };
            onUpdate({ approvalLabelId: value || undefined, approvalLabel });
          }}
        />
      </ApprovalSetting>

      <ApprovalSetting label="승인자 기준">
        <CompactSelect
          label={`${step.label} 승인자 기준`}
          value={step.resolverType}
          options={resolverOptions}
          disabled={disabled}
          onChange={(value) => {
            const resolverType = value as ResolverType;
            onUpdate({ resolverType, resolverConfig: defaultResolverConfig(resolverType) });
          }}
        />
      </ApprovalSetting>

      {(step.resolverType === 'REQUESTER_ORG_LEADER' || step.resolverType === 'SUBJECT_ORG_LEADER') ? (
        <ApprovalSetting label="조직 단계">
          <CompactInput
            label={`${step.label} 조직 단계`}
            type="number"
            value={Number(config.level ?? 1)}
            minimum={1}
            disabled={disabled}
            onChange={(value) => updateConfig({ level: Math.max(1, Number(value) || 1) })}
          />
        </ApprovalSetting>
      ) : null}

      {(step.resolverType === 'SUBJECT_ORG_LEADER' || step.resolverType === 'FORM_PERSON_FIELD') ? (
        <ApprovalSetting label="구성원 필드">
          <CompactSelect
            label={`${step.label} 구성원 필드`}
            value={fieldKey}
            options={personFields.map((field) => ({ value: field.key, label: field.label }))}
            disabled={disabled}
            onChange={(value) => updateConfig({ fieldKey: value })}
          />
        </ApprovalSetting>
      ) : null}

      {step.resolverType === 'FIXED_PERSON' ? (
        <ApprovalSetting label="구성원">
          {renderFixedPersonPicker ? renderFixedPersonPicker({
            step,
            stepIndex: index,
            personID,
            preview,
            disabled,
            onChange: (value) => onUpdate({ resolverConfig: { personId: value } }),
          }) : (
            <Alert severity="warning">구성원 검색을 불러오지 못했습니다.</Alert>
          )}
        </ApprovalSetting>
      ) : null}

      {step.resolverType === 'FIXED_ROLE' ? (
        <ApprovalSetting label="승인 역할">
          {renderFixedRolePicker ? renderFixedRolePicker({
            step,
            stepIndex: index,
            roleCode,
            preview,
            disabled,
            onChange: (value) => onUpdate({ resolverConfig: { roleCode: value } }),
          }) : <Alert severity="warning">승인 역할 검색을 불러오지 못했습니다.</Alert>}
        </ApprovalSetting>
      ) : null}

      <ApprovalSetting label="승인자를 못 찾으면">
        <CompactSelect
          label={`${step.label} 승인자를 못 찾을 때`}
          value={step.missingPolicy}
          options={[
            { value: 'FAIL', label: '제출 차단' },
            { value: 'SKIP', label: '단계 생략' },
            { value: 'FALLBACK_ROOT_ORG_LEADER', label: '최상위 조직장으로 대체' },
          ]}
          disabled={disabled}
          onChange={(value) => onUpdate({ missingPolicy: value as WorkflowRequestDraftApprovalStep['missingPolicy'] })}
        />
      </ApprovalSetting>

      <ApprovalSetting label="본인이 승인자면">
        <CompactSelect
          label={`${step.label} 본인이 승인자일 때`}
          value={step.selfPolicy}
          options={[{ value: 'SKIP', label: '단계 생략' }, { value: 'FAIL', label: '제출 차단' }]}
          disabled={disabled}
          onChange={(value) => onUpdate({ selfPolicy: value as WorkflowRequestDraftApprovalStep['selfPolicy'] })}
        />
      </ApprovalSetting>

      <ApprovalSetting label="앞 단계와 같으면">
        <CompactSelect
          label={`${step.label} 앞 단계와 같을 때`}
          value={step.duplicatePolicy}
          options={[{ value: 'SKIP', label: '중복 생략' }, { value: 'KEEP', label: '그대로 유지' }]}
          disabled={disabled}
          onChange={(value) => onUpdate({ duplicatePolicy: value as WorkflowRequestDraftApprovalStep['duplicatePolicy'] })}
        />
      </ApprovalSetting>

      <ApprovalSetting label="처리 기한(분)">
        <CompactInput
          label={`${step.label} 처리 기한`}
          type="number"
          value={step.dueAfterMinutes ?? ''}
          minimum={1}
          disabled={disabled}
          onChange={(value) => onUpdate({ dueAfterMinutes: value ? Math.max(1, Number(value)) : undefined })}
        />
      </ApprovalSetting>
    </Box>
  );
}

/** 제출 전 예상 승인자와 요청별 resolver 편집을 한 개의 연결된 승인 graph로 보여줍니다. */
export function WorkflowApprovalLineEditor({
  steps,
  onChange,
  fields = [],
  previews = [],
  approvalLabels = [{ name: '승인', tone: 'GREEN' }],
  disabled = false,
  error,
  getStepError,
  minimumSteps = 1,
  showAddStep = true,
  showStepActions = true,
  onAddStep,
  renderFixedPersonPicker,
  renderFixedRolePicker,
  action,
  description,
  title = '결재선',
  hideTitle = false,
  flowMinHeight = 160,
  sx,
}: WorkflowApprovalLineEditorProps) {
  const generatedID = useId();
  const headingID = `workflow-approval-line-${normalizeDOMID(generatedID)}`;
  const showHeader = !hideTitle || Boolean(description) || showAddStep || showStepActions;
  const hasContentBeforeFlow = showHeader || Boolean(error);
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(() => new Set());
  const [stepMenu, setStepMenu] = useState<{ key: string; anchorElement: HTMLElement } | null>(null);
  const [draggedStepIndex, setDraggedStepIndex] = useState<number | null>(null);
  const [dragOverStepIndex, setDragOverStepIndex] = useState<number | null>(null);
  const [dragAnnouncement, setDragAnnouncement] = useState('');
  const blockCardDragRef = useRef(false);
  const stepPreviews = useMemo(
    () => steps.map((step, index) => previewForStep(previews, step, index)),
    [previews, steps],
  );

  const updateStep = (index: number, patch: Partial<WorkflowRequestDraftApprovalStep>) => {
    if (disabled || !steps[index]?.requesterEditable) return;
    onChange(steps.map((step, current) => current === index ? { ...step, ...patch } : { ...step }));
  };
  const canMoveStep = (index: number, target: number) => (
    !disabled
    && Boolean(steps[index]?.requesterEditable)
    && Boolean(steps[target]?.requesterEditable)
    && target >= 0
    && target < steps.length
    && steps[index]?.stageOrder === steps[target]?.stageOrder
  );
  const moveStep = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (!canMoveStep(index, target)) return;
    const next = steps.map((step) => ({ ...step }));
    [next[index], next[target]] = [next[target], next[index]];
    onChange(normalizeWorkflowApprovalSteps(next));
    setDragAnnouncement(`${steps[index].label} 단계를 ${target + 1}번째로 이동했습니다.`);
  };
  const canDropStep = (source: number, target: number) => {
    if (!canMoveStep(source, target) && source !== target) return false;
    if (source === target) return Boolean(steps[source]?.requesterEditable) && !disabled;
    const start = Math.min(source, target);
    const end = Math.max(source, target);
    return steps.slice(start, end + 1).every((step) => step.requesterEditable);
  };
  const reorderStep = (source: number, target: number) => {
    if (!canDropStep(source, target) || source === target) return;
    const next = steps.map((step) => ({ ...step }));
    const [moved] = next.splice(source, 1);
    next.splice(target, 0, moved);
    onChange(normalizeWorkflowApprovalSteps(next));
    setDragAnnouncement(`${moved.label} 단계를 ${target + 1}번째로 이동했습니다.`);
  };
  const finishDragging = () => {
    blockCardDragRef.current = false;
    setDraggedStepIndex(null);
    setDragOverStepIndex(null);
  };
  const removeStep = (index: number) => {
    if (disabled || !steps[index]?.requesterEditable || steps.length <= minimumSteps) return;
    const removedLabel = steps[index].label;
    onChange(normalizeWorkflowApprovalSteps(steps.filter((_, current) => current !== index)));
    setDragAnnouncement(`${removedLabel} 단계를 삭제했습니다.`);
  };
  const addStep = () => {
    if (disabled) return;
    if (onAddStep) {
      onAddStep();
      return;
    }
    onChange(normalizeWorkflowApprovalSteps([...steps.map((step) => ({ ...step })), newApprovalStep(steps.length + 1)]));
  };
  const addStepToStage = (stageOrder: number) => {
    if (disabled) return;
    onChange(normalizeWorkflowApprovalSteps(insertWorkflowApprovalStepInStage(
      steps,
      stageOrder,
      newApprovalStep(steps.length + 1),
    )));
  };
  const mergeStageWithPrevious = (stageOrder: number) => {
    if (disabled || steps.some((step) => step.stageOrder === stageOrder && !step.requesterEditable)) return;
    onChange(normalizeWorkflowApprovalSteps(mergeWorkflowApprovalStageWithPrevious(steps, stageOrder)));
    setDragAnnouncement(`${stageOrder}단계를 이전 단계와 합쳤습니다.`);
  };
  const canSplitStageAt = (index: number) => {
    if (disabled || index <= 0 || !steps[index]?.requesterEditable) return false;
    const stageOrder = steps[index].stageOrder;
    return steps.every((step, current) => current < index || step.stageOrder !== stageOrder || step.requesterEditable);
  };
  const splitStageAt = (index: number) => {
    if (!canSplitStageAt(index)) return;
    onChange(normalizeWorkflowApprovalSteps(splitWorkflowApprovalStageAt(steps, index)));
    setDragAnnouncement(`${steps[index].label} 승인자부터 다음 단계로 분리했습니다.`);
  };
  const approvalStageGroups = groupWorkflowApprovalSteps(steps);
  const canMoveStage = (stageOrder: number, direction: -1 | 1) => {
    if (disabled) return false;
    const sourceIndex = approvalStageGroups.findIndex((stage) => stage.stageOrder === stageOrder);
    const source = approvalStageGroups[sourceIndex];
    const target = approvalStageGroups[sourceIndex + direction];
    return Boolean(source && target && [...source.items, ...target.items].every(({ step }) => step.requesterEditable));
  };
  const moveStage = (stageOrder: number, direction: -1 | 1) => {
    if (!canMoveStage(stageOrder, direction)) return;
    onChange(normalizeWorkflowApprovalSteps(moveWorkflowApprovalStage(steps, stageOrder, direction)));
    setDragAnnouncement(`${stageOrder}단계를 ${direction < 0 ? '앞' : '뒤'}으로 이동했습니다.`);
  };
  const toggleExpanded = (key: string) => {
    setExpandedSteps((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const closeStepMenu = () => setStepMenu(null);

  return (
    <Box
      component="section"
      aria-labelledby={hideTitle ? undefined : headingID}
      aria-label={hideTitle ? title : undefined}
      sx={[{ minWidth: 0 }, ...sxOverrides(sx)]}
    >
      {showHeader ? <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.25}
        sx={{ alignItems: { sm: 'flex-start' }, justifyContent: 'space-between' }}
      >
        {!hideTitle || description || showStepActions ? <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>{!hideTitle ? <Typography
            id={headingID}
            component="h2"
            variant="h5"
            sx={designTokens.typography.sectionTitle}
          >{title}</Typography> : null}{showStepActions && <HelpPopover title={title}>병렬 승인자는 함께 시작하고 모두 승인하면 다음 단계로 진행합니다. 승인자 순서는 병렬 경로 안에서만 바꿀 수 있습니다.</HelpPopover>}</Stack>
          {description ? <Typography variant="body2" color="text.secondary" sx={{ mt: hideTitle ? 0 : 0.35 }}>{description}</Typography> : null}
        </Box> : null}
        {showAddStep ? (
          <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: 'wrap' }}>
            <Button size="small" startIcon={<AddOutlined />} disabled={disabled} onClick={addStep}>다음 단계 추가</Button>
          </Stack>
        ) : null}
      </Stack> : null}

      <Box aria-live="polite" sx={visuallyHiddenSx}>{dragAnnouncement}</Box>

      {error ? <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert> : null}

      <Box
        data-testid="workflow-approval-flow"
        sx={{
          mt: hasContentBeforeFlow ? 1.5 : 0,
          p: { xs: 1.25, sm: 1.25 },
          minHeight: { xs: 0, md: flowMinHeight },
          minWidth: 0,
          overflowX: { xs: 'visible', md: 'auto' },
          position: 'relative',
          borderRadius: designTokens.radius.surface,
          border: `1px solid ${designTokens.color.divider}`,
          bgcolor: designTokens.color.sidebarSurface,
          overscrollBehaviorX: 'contain',
        }}
      >
        {action ? (
          <Box
            data-testid="workflow-approval-flow-action"
            sx={{
              position: 'absolute',
              top: { xs: 10, sm: 12 },
              right: { xs: 10, sm: 12 },
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {action}
          </Box>
        ) : null}
        <Box
          component="ol"
          aria-label="제출 전 예상 결재 순서"
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'center' },
            m: 0,
            px: 0,
            pb: 0,
            pt: { md: showStepActions ? 5 : 0 },
            minHeight: { md: Math.max(0, flowMinHeight - 24) },
            minWidth: { md: 'max-content' },
            listStyle: 'none',
          }}
        >
          <Box component="li" sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box
              data-testid="workflow-approval-start"
              sx={{
                width: { xs: 112, md: 48 },
                minHeight: { xs: 44, md: 72 },
                px: 0.5,
                py: 0.5,
                display: 'flex',
                flexDirection: { xs: 'row', md: 'column' },
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
              }}
            >
              <Avatar
                aria-hidden="true"
                sx={{
                  width: 30,
                  height: 30,
                  flex: '0 0 auto',
                  bgcolor: designTokens.color.actionSoft,
                  color: designTokens.color.action,
                }}
              >
                <NotesOutlined sx={{ fontSize: 17 }} />
              </Avatar>
              <Typography
                variant="caption"
                sx={{ color: designTokens.color.secondaryText, fontWeight: 750, lineHeight: 1.2, textAlign: 'center' }}
              >요청 제출</Typography>
            </Box>
          </Box>

          {approvalStageGroups.map((stage) => {
            const stageExpanded = stage.items.some(({ step, index }) => expandedSteps.has(approvalStepKey(step, index)));
            const parallel = stage.items.length > 1;
            return <Box
            component="li"
            key={`workflow-approval-stage-${stage.stageOrder}`}
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'stretch', md: 'center' },
              minWidth: 0,
            }}
          >
            <WorkflowApprovalConnector compact={showStepActions} />
            <Box
              component="section"
              data-testid={`workflow-approval-stage-group-${stage.stageOrder}`}
              data-parallel={parallel ? 'true' : undefined}
              aria-label={`${stage.stageOrder}단계 승인자 ${stage.items.length}명`}
              aria-description={parallel ? '모든 승인자가 승인하면 다음 단계로 진행' : undefined}
              sx={{
                position: 'relative',
                width: { xs: '100%', md: stageExpanded ? parallel ? 456 : 400 : parallel ? 246 : 190 },
                flexShrink: 0,
              }}
            >
              {showStepActions ? <Stack
                direction="row"
                spacing={0.5}
                useFlexGap
                sx={{
                  mb: { xs: 0.75, md: 0 },
                  position: { md: 'absolute' },
                  top: { md: -36 },
                  right: { md: parallel ? 28 : 0 },
                  zIndex: 1,
                  flexWrap: 'wrap',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  whiteSpace: 'nowrap',
                }}
              >
                  <Tooltip title="앞 단계로 이동">
                    <span><IconButton size="small" aria-label={`${stage.stageOrder}단계 앞으로 이동`} disabled={!canMoveStage(stage.stageOrder, -1)} onClick={() => moveStage(stage.stageOrder, -1)}><ArrowBackOutlined fontSize="small" /></IconButton></span>
                  </Tooltip>
                  <Tooltip title="뒤 단계로 이동">
                    <span><IconButton size="small" aria-label={`${stage.stageOrder}단계 뒤로 이동`} disabled={!canMoveStage(stage.stageOrder, 1)} onClick={() => moveStage(stage.stageOrder, 1)}><ArrowForwardOutlined fontSize="small" /></IconButton></span>
                  </Tooltip>
                  {stage.stageOrder > 1 ? <Tooltip title="앞 단계와 병렬로 묶기">
                    <span><IconButton
                      size="small"
                      aria-label="이전 단계와 합치기"
                      disabled={disabled || stage.items.some(({ step }) => !step.requesterEditable)}
                      onClick={() => mergeStageWithPrevious(stage.stageOrder)}
                    ><CallMergeOutlined fontSize="small" /></IconButton></span>
                  </Tooltip> : null}
                  <Tooltip title="병렬 승인자 추가">
                    <span><IconButton size="small" aria-label="같은 단계 승인자 추가" disabled={disabled} onClick={() => addStepToStage(stage.stageOrder)}><AddOutlined fontSize="small" /></IconButton></span>
                  </Tooltip>
              </Stack> : null}
              <Box
                sx={{
                  position: 'relative',
                  display: 'grid',
                  width: '100%',
                  gridTemplateColumns: parallel ? '28px minmax(0, 1fr) 28px' : 'minmax(0, 1fr)',
                  rowGap: 0.5,
                  alignItems: 'stretch',
                }}
              >
                {parallel ? <>
                  <Box aria-hidden="true" sx={{ position: 'absolute', top: '50%', left: 0, width: 14, height: 2, bgcolor: designTokens.color.strongDivider, transform: 'translateY(-50%)' }} />
                  <Box aria-hidden="true" sx={{ position: 'absolute', top: '50%', right: 0, width: 14, height: 2, bgcolor: designTokens.color.strongDivider, transform: 'translateY(-50%)' }} />
                </> : null}
          {stage.items.map(({ step, index }, memberIndex) => {
          const key = approvalStepKey(step, index);
          const expanded = expandedSteps.has(key);
          const editable = !disabled && step.requesterEditable;
          const preview = stepPreviews[index];
          const teamResolver = step.resolverType === 'FIXED_ROLE';
          const previewName = preview?.loading
            ? '승인자를 확인하는 중'
            : teamResolver
              ? preview?.name || preview?.fallbackLabel || '승인 역할 확인 필요'
              : preview?.name || preview?.fallbackLabel || '제출할 때 승인자 확정';
          const previewMeta = teamResolver
            ? preview?.note || '지정한 팀·역할의 승인자'
            : preview?.name
              ? preview.jobTitle || preview.note || resolverDescription(step)
              : preview?.note || resolverDescription(step);
          const initials = preview?.name ? Array.from(preview.name)[0] : String(index + 1);
          const stepError = getStepError?.(step, index);
          const approvalLabel = step.approvalLabel
            ?? approvalLabels.find((label) => (label.id ?? '') === (step.approvalLabelId ?? ''))
            ?? approvalLabels[0]
            ?? { name: '승인', tone: 'GREEN' as const };
          const approvalTone = workflowApprovalLabelBadgeTone[approvalLabel.tone];
          return (
            <Box
              key={key}
              sx={{ display: 'contents' }}
            >
              {parallel ? <WorkflowApprovalBranchRail side="fork" index={memberIndex} count={stage.items.length} /> : null}
              <Box sx={{ gridColumn: parallel ? 2 : 1, gridRow: memberIndex + 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch', minWidth: 0 }}>
              <Box
                component="article"
                draggable={showStepActions && editable}
                data-testid="workflow-approval-node"
                data-drop-active={dragOverStepIndex === index ? 'true' : undefined}
                data-dragging={draggedStepIndex === index ? 'true' : undefined}
                aria-label={`${index + 1}단계 예상 결재자 ${previewName}`}
                onPointerDownCapture={(event) => {
                  blockCardDragRef.current = isInteractiveDragOrigin(event.target);
                }}
                onPointerUpCapture={() => {
                  blockCardDragRef.current = false;
                }}
                onPointerCancelCapture={() => {
                  blockCardDragRef.current = false;
                }}
                onDragStart={(event) => {
                  if (blockCardDragRef.current || isInteractiveDragOrigin(event.target)) {
                    event.preventDefault();
                    return;
                  }
                  event.dataTransfer.effectAllowed = 'move';
                  event.dataTransfer.setData('text/plain', key);
                  setDraggedStepIndex(index);
                  setDragAnnouncement(`${step.label} 단계를 이동하고 있습니다.`);
                }}
                onDragEnd={finishDragging}
                onDragOver={(event) => {
                  if (draggedStepIndex === null || !canDropStep(draggedStepIndex, index)) return;
                  event.preventDefault();
                  event.dataTransfer.dropEffect = 'move';
                  setDragOverStepIndex(index);
                }}
                onDragLeave={(event) => {
                  if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragOverStepIndex(null);
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  if (draggedStepIndex !== null) reorderStep(draggedStepIndex, index);
                  finishDragging();
                }}
                sx={{
                  width: { xs: '100%', md: expanded ? 400 : 190 },
                  minHeight: { md: showStepActions ? (expanded ? 136 : 112) : 'auto' },
                  p: { xs: 1.5, md: 1.125 },
                  minWidth: 0,
                  cursor: showStepActions && editable ? 'grab' : 'default',
                  borderRadius: designTokens.radius.surface,
                  border: `1px solid ${stepError ? designTokens.color.danger : dragOverStepIndex === index ? designTokens.color.action : designTokens.color.divider}`,
                  bgcolor: dragOverStepIndex === index ? designTokens.color.actionSoft : designTokens.color.canvas,
                  boxShadow: dragOverStepIndex === index ? `0 0 0 3px ${designTokens.color.action}` : designTokens.shadow.surface,
                  transition: 'border-color 140ms ease, box-shadow 140ms ease, width 160ms ease',
                  '&:active': {
                    cursor: showStepActions && editable ? 'grabbing' : 'default',
                  },
                  '&:focus-within': {
                    borderColor: stepError ? designTokens.color.danger : designTokens.color.action,
                  },
                  '@media (prefers-reduced-motion: reduce)': {
                    transition: 'none',
                  },
                }}
              >
                <Stack
                  direction="row"
                  spacing={0.5}
                  sx={{
                    minHeight: showStepActions && editable ? 24 : 'auto',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                  }}
                >
                  {showStepActions && editable ? (
                    <Select<string>
                      aria-label={`${step.label} 승인 라벨`}
                      data-testid={`workflow-approval-label-select-${index}`}
                      variant="standard"
                      disableUnderline
                      value={step.approvalLabelId ?? step.approvalLabel?.id ?? ''}
                      onChange={(event) => {
                        const value = event.target.value;
                        const nextLabel = approvalLabels.find((label) => (label.id ?? '') === value)
                          ?? { name: '승인', tone: 'GREEN' as const };
                        updateStep(index, { approvalLabelId: value || undefined, approvalLabel: nextLabel });
                      }}
                      renderValue={() => (
                        <ValueBadge
                          size="compact"
                          tone={approvalTone}
                          maxWidth={88}
                          title={approvalLabel.name}
                          label={<Stack component="span" direction="row" spacing={0.25} sx={{ minWidth: 0, alignItems: 'center' }}><Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{approvalLabel.name}</Box><ExpandMoreOutlined sx={{ fontSize: 13, flexShrink: 0 }} /></Stack>}
                        />
                      )}
                      sx={{ minWidth: 0, '& .MuiSelect-select': { p: 0, display: 'flex', alignItems: 'center' }, '& .MuiSelect-icon': { display: 'none' } }}
                    >
                      {approvalLabels.map((label, labelIndex) => (
                        <MenuItem key={label.id ?? `builtin-${labelIndex}`} value={label.id ?? ''}><ValueBadge label={label.name} /></MenuItem>
                      ))}
                    </Select>
                  ) : (
                    <Stack direction="row" spacing={0.25} sx={{ minWidth: 0, flex: 1, alignItems: 'flex-start' }}>
                      {!step.requesterEditable ? (
                        <Tooltip title="양식에서 고정한 단계 · 수정 불가" describeChild>
                          <Box
                            component="span"
                            role="img"
                            aria-label="양식에서 고정한 단계, 수정 불가"
                            sx={{ mt: '2px', display: 'inline-flex', flex: '0 0 auto', color: designTokens.color.secondaryText }}
                          >
                            <LockOutlined aria-hidden="true" sx={{ fontSize: 13 }} />
                          </Box>
                        </Tooltip>
                      ) : null}
                      <Tooltip title={step.label} enterDelay={500}>
                        <Typography
                          component="h3"
                          variant="body2"
                          sx={{ minWidth: 0, flex: 1, fontSize: { md: 13 }, fontWeight: 800, lineHeight: 1.35, wordBreak: 'keep-all', overflowWrap: 'anywhere', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                        >{step.label}</Typography>
                      </Tooltip>
                    </Stack>
                  )}
                  {showStepActions && editable ? (
                    <>
                      <Tooltip title="단계 더보기">
                        <IconButton
                          size="small"
                          aria-label={`${step.label} 더보기`}
                          aria-haspopup="menu"
                          aria-controls={stepMenu?.key === key ? `${headingID}-step-${index}-menu` : undefined}
                          aria-expanded={stepMenu?.key === key ? 'true' : undefined}
                          onClick={(event) => setStepMenu({ key, anchorElement: event.currentTarget })}
                        >
                          <MoreVertOutlined />
                        </IconButton>
                      </Tooltip>
                      <AnchoredMenu
                        id={`${headingID}-step-${index}-menu`}
                        anchorEl={stepMenu?.key === key ? stepMenu.anchorElement : null}
                        open={stepMenu?.key === key}
                        onClose={closeStepMenu}
                      >
                        <MenuItem onClick={() => {
                          closeStepMenu();
                          toggleExpanded(key);
                        }}>{expanded ? '상세 정보 닫기' : '상세 정보 수정'}</MenuItem>
                        <MenuItem disabled={!canMoveStep(index, index - 1)} onClick={() => {
                          closeStepMenu();
                          moveStep(index, -1);
                        }}>같은 단계에서 위로 이동</MenuItem>
                        <MenuItem disabled={!canMoveStep(index, index + 1)} onClick={() => {
                          closeStepMenu();
                          moveStep(index, 1);
                        }}>같은 단계에서 아래로 이동</MenuItem>
                        {memberIndex > 0 ? <MenuItem disabled={!canSplitStageAt(index)} onClick={() => {
                          closeStepMenu();
                          splitStageAt(index);
                        }}>이 승인자부터 다음 단계로 분리</MenuItem> : null}
                        <MenuItem
                          disabled={steps.length <= minimumSteps}
                          onClick={() => {
                            closeStepMenu();
                            removeStep(index);
                          }}
                          sx={{ color: designTokens.color.danger }}
                        >단계 삭제</MenuItem>
                      </AnchoredMenu>
                    </>
                  ) : (
                    <ValueBadge
                      size="compact"
                      tone={approvalTone}
                      maxWidth={72}
                      data-testid={`workflow-approval-label-chip-${index}`}
                      label={approvalLabel.name}
                    />
                  )}
                </Stack>

                {showStepActions && editable ? (
                  <Box sx={{ mt: 0.5, minWidth: 0, width: '100%' }}>
                    <Tooltip title={step.label} enterDelay={500}>
                      <Typography
                        component="h3"
                        variant="body2"
                        sx={{ minWidth: 0, fontWeight: 800, lineHeight: 1.35, wordBreak: 'keep-all', overflowWrap: 'anywhere', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                      >{step.label}</Typography>
                    </Tooltip>
                  </Box>
                ) : null}

                <Stack direction="row" spacing={1} sx={{ mt: showStepActions ? 1 : 0.5, alignItems: 'center', minWidth: 0 }}>
                  <Avatar
                    alt=""
                    src={teamResolver ? undefined : preview?.profilePhotoURL}
                    sx={{
                      width: 38,
                      height: 38,
                      flex: '0 0 auto',
                      bgcolor: preview?.name || teamResolver ? designTokens.color.actionSoft : designTokens.color.subtleSurface,
                      color: preview?.name || teamResolver ? designTokens.color.action : designTokens.color.secondaryText,
                      fontSize: 13,
                      fontWeight: 800,
                      boxShadow: `0 0 0 2px ${designTokens.color.actionSoft}`,
                    }}
                  >
                    {teamResolver ? <AccountTreeOutlined sx={{ fontSize: 19 }} /> : initials}
                  </Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    {preview?.loading ? <CircularProgress size={18} aria-label={preview.note || previewName} /> : <>
                    <Typography variant="body2" sx={{ minWidth: 0, fontWeight: 800 }} noWrap>{previewName}</Typography>
                    <Tooltip title={previewMeta} enterDelay={500}>
                      <Typography component="span" variant="caption" noWrap sx={{ mt: 0.1, display: 'block', minWidth: 0, color: designTokens.color.secondaryText }}>{previewMeta}</Typography>
                    </Tooltip>
                    </>}
                  </Box>
                </Stack>

                {stepError ? <Typography role="alert" variant="caption" sx={{ mt: 1, display: 'block', color: designTokens.color.danger }}>{stepError}</Typography> : null}

                {step.requesterEditable ? (
                  <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <ApprovalStepDetails
                      step={step}
                      index={index}
                      fields={fields}
                      preview={preview}
                      approvalLabels={approvalLabels}
                      disabled={disabled}
                      onUpdate={(patch) => updateStep(index, patch)}
                      renderFixedPersonPicker={renderFixedPersonPicker}
                      renderFixedRolePicker={renderFixedRolePicker}
                    />
                  </Collapse>
                ) : null}
              </Box>
              </Box>
              {parallel ? <WorkflowApprovalBranchRail side="join" index={memberIndex} count={stage.items.length} /> : null}
            </Box>
          );
        })}
              </Box>
            </Box>
          </Box>;
          })}

          <Box
            component="li"
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
            }}
          >
            <WorkflowApprovalConnector compact={showStepActions} />
            <Box
              data-testid="workflow-approval-end"
              sx={{
                width: { xs: 112, md: 48 },
                minHeight: { xs: 44, md: 72 },
                px: 0.5,
                py: 0.5,
                display: 'flex',
                flexDirection: { xs: 'row', md: 'column' },
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
              }}
            >
              <Avatar
                aria-hidden="true"
                sx={{
                  width: 30,
                  height: 30,
                  flex: '0 0 auto',
                  bgcolor: designTokens.color.successSoft,
                  color: designTokens.color.successDark,
                }}
              >
                <CheckCircleOutlined sx={{ fontSize: 17 }} />
              </Avatar>
              <Typography
                variant="caption"
                sx={{ color: designTokens.color.secondaryText, fontWeight: 750, lineHeight: 1.2, textAlign: 'center' }}
              >승인 완료</Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
